import type {
    ApiSpinTogetherActivityState,
    ApiSpinTogetherFriendStateAny,
    ApiSpinTogetherLiveActivity,
    ApiSpinTogetherSettledOutcome,
} from "@bridge-applications/pb-types";

export const SPIN_TOGETHER_TRANSIENT_ACTIVITY_MS = 10_000;
export const SPIN_TOGETHER_DEFAULT_OUTCOME_DISPLAY_SECONDS = 5;
export const SPIN_TOGETHER_DEFAULT_MAXIMUM_OUTCOME_AGE_SECONDS = 60;
const SPIN_TOGETHER_MAX_OUTCOME_PRESENTATIONS = 100;

export interface SpinTogetherOutcomePresentation {
    friendUserId: string;
    outcome: ApiSpinTogetherSettledOutcome;
    receivedAtMs: number;
}

export function spinTogetherActivityIsAtLeastAsRecent(
    current: ApiSpinTogetherActivityState | null,
    incoming: ApiSpinTogetherActivityState,
): boolean {
    return (
        current === null ||
        Date.parse(incoming.occurredAt) >= Date.parse(current.occurredAt)
    );
}

export function spinTogetherActivityIsFresh(
    activity: ApiSpinTogetherActivityState | null,
    nowMs: number,
): boolean {
    if (!activity) return false;
    const occurredAtMs = Date.parse(activity.occurredAt);
    return (
        Number.isFinite(occurredAtMs) &&
        occurredAtMs <= nowMs &&
        nowMs - occurredAtMs < SPIN_TOGETHER_TRANSIENT_ACTIVITY_MS
    );
}

export function spinTogetherLiveActivityIsAtLeastAsRecent(
    current: ApiSpinTogetherLiveActivity | null | undefined,
    incoming: ApiSpinTogetherLiveActivity,
): boolean {
    return (
        !current ||
        Date.parse(incoming.occurredAt) >= Date.parse(current.occurredAt)
    );
}

export function spinTogetherLiveActivityIsFresh(
    activity: ApiSpinTogetherLiveActivity | null | undefined,
    nowMs: number,
): boolean {
    if (!activity) return false;
    const occurredAtMs = Date.parse(activity.occurredAt);
    return (
        Number.isFinite(occurredAtMs) &&
        occurredAtMs <= nowMs &&
        nowMs - occurredAtMs < SPIN_TOGETHER_TRANSIENT_ACTIVITY_MS
    );
}

export function spinTogetherOutcomeIsAtLeastAsRecent(
    current: ApiSpinTogetherSettledOutcome | null | undefined,
    incoming: ApiSpinTogetherSettledOutcome,
): boolean {
    return (
        !current ||
        Date.parse(incoming.settledAt) >= Date.parse(current.settledAt)
    );
}

export function spinTogetherOutcomeIsWithinMaximumAge(
    outcome: ApiSpinTogetherSettledOutcome | null | undefined,
    nowMs: number,
    maximumAgeSeconds: number = SPIN_TOGETHER_DEFAULT_MAXIMUM_OUTCOME_AGE_SECONDS,
): boolean {
    if (!outcome) return false;
    const settledAtMs = Date.parse(outcome.settledAt);
    const ageMs = nowMs - settledAtMs;
    return (
        Number.isFinite(settledAtMs) &&
        ageMs >= 0 &&
        ageMs <= maximumAgeSeconds * 1_000
    );
}

export function spinTogetherOutcomePresentationIsFresh(
    presentation: SpinTogetherOutcomePresentation | null | undefined,
    nowMs: number,
    displaySeconds: number = SPIN_TOGETHER_DEFAULT_OUTCOME_DISPLAY_SECONDS,
): boolean {
    if (!presentation) return false;
    const ageMs = nowMs - presentation.receivedAtMs;
    return (
        Number.isFinite(presentation.receivedAtMs) &&
        ageMs >= 0 &&
        ageMs < displaySeconds * 1_000
    );
}

export function recordSpinTogetherOutcomePresentation(
    current: SpinTogetherOutcomePresentation[],
    friendUserId: string,
    outcome: ApiSpinTogetherSettledOutcome,
    receivedAtMs: number,
    maximumAgeSeconds: number = SPIN_TOGETHER_DEFAULT_MAXIMUM_OUTCOME_AGE_SECONDS,
): SpinTogetherOutcomePresentation[] {
    if (
        outcome.result !== "WIN" ||
        outcome.prizes.length === 0 ||
        !spinTogetherOutcomeIsWithinMaximumAge(
            outcome,
            receivedAtMs,
            maximumAgeSeconds,
        ) ||
        current.some(
            (presentation) => presentation.outcome.eventId === outcome.eventId,
        )
    ) {
        return current;
    }
    return [{ friendUserId, outcome, receivedAtMs }, ...current].slice(
        0,
        SPIN_TOGETHER_MAX_OUTCOME_PRESENTATIONS,
    );
}

export function preserveNewerSpinTogetherFriendState(
    current: ApiSpinTogetherFriendStateAny | undefined,
    incoming: ApiSpinTogetherFriendStateAny,
): ApiSpinTogetherFriendStateAny {
    if (!current) return incoming;
    return {
        ...incoming,
        activity:
            current.activity &&
            incoming.activity &&
            !spinTogetherActivityIsAtLeastAsRecent(
                current.activity,
                incoming.activity,
            )
                ? current.activity
                : incoming.activity,
        ...(incoming.liveActivity !== undefined
            ? {
                  liveActivity:
                      current.liveActivity &&
                      incoming.liveActivity &&
                      !spinTogetherLiveActivityIsAtLeastAsRecent(
                          current.liveActivity,
                          incoming.liveActivity,
                      )
                          ? current.liveActivity
                          : incoming.liveActivity,
              }
            : {}),
        ...(incoming.latestOutcome !== undefined
            ? {
                  latestOutcome:
                      current.latestOutcome &&
                      incoming.latestOutcome &&
                      !spinTogetherOutcomeIsAtLeastAsRecent(
                          current.latestOutcome,
                          incoming.latestOutcome,
                      )
                          ? current.latestOutcome
                          : incoming.latestOutcome,
              }
            : {}),
    };
}

export const preserveNewerSpinTogetherActivity =
    preserveNewerSpinTogetherFriendState;
